import { useState, useEffect, useCallback } from 'react';
import { Building } from '../types/building';
import { User } from '../types/user';
import { Photo } from '../types/photo';
import * as IndexedDB from '../lib/indexedDB';

export const useIndexedDB = () => {
  const [isSupported, setIsSupported] = useState<boolean>(true);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [migrationNeeded, setMigrationNeeded] = useState<boolean>(false);
  const [migrationComplete, setMigrationComplete] = useState<boolean>(false);
  const [migrationProgress, setMigrationProgress] = useState<number>(0);
  const [migrationMessage, setMigrationMessage] = useState<string>('');

  // Initialize IndexedDB
  useEffect(() => {
    const init = async () => {
      try {
        // Check if IndexedDB is supported
        const supported = IndexedDB.isIndexedDBSupported();
        setIsSupported(supported);
        
        if (!supported) {
          setError('IndexedDB is not supported in this browser');
          return;
        }
        
        // Check if migration is needed
        const needsMigration = IndexedDB.isMigrationNeeded();
        setMigrationNeeded(needsMigration);
        
        // Check if migration is complete
        const migrationDone = IndexedDB.isMigrationComplete();
        setMigrationComplete(migrationDone);
        
        // Initialize database
        await IndexedDB.getDB();
        setIsInitialized(true);
      } catch (err) {
        console.error('Error initializing IndexedDB:', err);
        setError(err instanceof Error ? err.message : 'Unknown error initializing IndexedDB');
      }
    };
    
    init();
  }, []);

  // Migrate from localStorage to IndexedDB
  const migrateFromLocalStorage = useCallback(async (): Promise<boolean> => {
    try {
      setError(null);
      
      const success = await IndexedDB.migrateFromLocalStorage((progress, message) => {
        setMigrationProgress(progress);
        setMigrationMessage(message);
      });
      
      if (success) {
        setMigrationComplete(true);
        setMigrationNeeded(false);
      }
      
      return success;
    } catch (err) {
      console.error('Error migrating from localStorage to IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error during migration');
      return false;
    }
  }, []);

  // Clean up localStorage after successful migration
  const cleanupLocalStorage = useCallback(async (): Promise<void> => {
    try {
      await IndexedDB.cleanupLocalStorage();
    } catch (err) {
      console.error('Error cleaning up localStorage:', err);
      setError(err instanceof Error ? err.message : 'Unknown error cleaning up localStorage');
    }
  }, []);

  // Get all buildings
  const getBuildings = useCallback(async (): Promise<Building[]> => {
    try {
      return await IndexedDB.getBuildings();
    } catch (err) {
      console.error('Error getting buildings from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error getting buildings');
      return [];
    }
  }, []);

  // Get all archived buildings
  const getArchivedBuildings = useCallback(async (): Promise<Building[]> => {
    try {
      return await IndexedDB.getArchivedBuildings();
    } catch (err) {
      console.error('Error getting archived buildings from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error getting archived buildings');
      return [];
    }
  }, []);

  // Get a building by ID
  const getBuilding = useCallback(async (id: string, archived = false): Promise<Building | null> => {
    try {
      return await IndexedDB.getBuilding(id, archived);
    } catch (err) {
      console.error('Error getting building from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error getting building');
      return null;
    }
  }, []);

  // Save a building
  const saveBuilding = useCallback(async (building: Building): Promise<void> => {
    try {
      await IndexedDB.saveBuilding(building);
    } catch (err) {
      console.error('Error saving building to IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error saving building');
    }
  }, []);

  // Delete a building
  const deleteBuilding = useCallback(async (id: string, archived = false): Promise<void> => {
    try {
      await IndexedDB.deleteBuilding(id, archived);
    } catch (err) {
      console.error('Error deleting building from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error deleting building');
    }
  }, []);

  // Archive a building
  const archiveBuilding = useCallback(async (id: string): Promise<Building | null> => {
    try {
      return await IndexedDB.archiveBuilding(id);
    } catch (err) {
      console.error('Error archiving building in IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error archiving building');
      return null;
    }
  }, []);

  // Unarchive a building
  const unarchiveBuilding = useCallback(async (id: string): Promise<Building | null> => {
    try {
      return await IndexedDB.unarchiveBuilding(id);
    } catch (err) {
      console.error('Error unarchiving building in IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error unarchiving building');
      return null;
    }
  }, []);

  // Get all users
  const getUsers = useCallback(async (): Promise<User[]> => {
    try {
      return await IndexedDB.getUsers();
    } catch (err) {
      console.error('Error getting users from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error getting users');
      return [];
    }
  }, []);

  // Get a user by ID
  const getUser = useCallback(async (id: string): Promise<User | null> => {
    try {
      return await IndexedDB.getUser(id);
    } catch (err) {
      console.error('Error getting user from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error getting user');
      return null;
    }
  }, []);

  // Get a user by email
  const getUserByEmail = useCallback(async (email: string): Promise<User | null> => {
    try {
      return await IndexedDB.getUserByEmail(email);
    } catch (err) {
      console.error('Error getting user by email from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error getting user by email');
      return null;
    }
  }, []);

  // Save a user
  const saveUser = useCallback(async (user: User): Promise<void> => {
    try {
      await IndexedDB.saveUser(user);
    } catch (err) {
      console.error('Error saving user to IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error saving user');
    }
  }, []);

  // Save multiple users
  const saveUsers = useCallback(async (users: User[]): Promise<void> => {
    try {
      await IndexedDB.saveUsers(users);
    } catch (err) {
      console.error('Error saving users to IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error saving users');
    }
  }, []);

  // Delete a user
  const deleteUser = useCallback(async (id: string): Promise<void> => {
    try {
      await IndexedDB.deleteUser(id);
    } catch (err) {
      console.error('Error deleting user from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error deleting user');
    }
  }, []);

  // Get all photos
  const getPhotos = useCallback(async (): Promise<Photo[]> => {
    try {
      return await IndexedDB.getPhotos();
    } catch (err) {
      console.error('Error getting photos from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error getting photos');
      return [];
    }
  }, []);

  // Get photos by apartment
  const getPhotosByApartment = useCallback(async (apartmentId: string): Promise<Photo[]> => {
    try {
      return await IndexedDB.getPhotosByApartment(apartmentId);
    } catch (err) {
      console.error('Error getting photos by apartment from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error getting photos by apartment');
      return [];
    }
  }, []);

  // Get photos by building
  const getPhotosByBuilding = useCallback(async (buildingId: string): Promise<Photo[]> => {
    try {
      return await IndexedDB.getPhotosByBuilding(buildingId);
    } catch (err) {
      console.error('Error getting photos by building from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error getting photos by building');
      return [];
    }
  }, []);

  // Save a photo
  const savePhoto = useCallback(async (photo: Photo): Promise<void> => {
    try {
      await IndexedDB.savePhoto(photo);
    } catch (err) {
      console.error('Error saving photo to IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error saving photo');
    }
  }, []);

  // Delete a photo
  const deletePhoto = useCallback(async (id: string): Promise<void> => {
    try {
      await IndexedDB.deletePhoto(id);
    } catch (err) {
      console.error('Error deleting photo from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error deleting photo');
    }
  }, []);

  // Get a setting
  const getSetting = useCallback(async (key: string): Promise<any> => {
    try {
      return await IndexedDB.getSetting(key);
    } catch (err) {
      console.error('Error getting setting from IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error getting setting');
      return null;
    }
  }, []);

  // Save a setting
  const saveSetting = useCallback(async (key: string, value: any): Promise<void> => {
    try {
      await IndexedDB.saveSetting(key, value);
    } catch (err) {
      console.error('Error saving setting to IndexedDB:', err);
      setError(err instanceof Error ? err.message : 'Unknown error saving setting');
    }
  }, []);

  return {
    isSupported,
    isInitialized,
    error,
    migrationNeeded,
    migrationComplete,
    migrationProgress,
    migrationMessage,
    migrateFromLocalStorage,
    cleanupLocalStorage,
    getBuildings,
    getArchivedBuildings,
    getBuilding,
    saveBuilding,
    deleteBuilding,
    archiveBuilding,
    unarchiveBuilding,
    getUsers,
    getUser,
    getUserByEmail,
    saveUser,
    saveUsers,
    deleteUser,
    getPhotos,
    getPhotosByApartment,
    getPhotosByBuilding,
    savePhoto,
    deletePhoto,
    getSetting,
    saveSetting,
  };
};